import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import i18n from "./setup/i18n";
import vuetify from "./setup/vuetify";
import SocioVueComponents, {
  setTelemetryUser,
  initializeTracingAndLogging,
} from "@socotec.io/socio-vue-components";
import { plugin as unleashPlugin, UnleashClient } from "@unleash/proxy-client-vue";
import unleashClient from "@/utils/unleashClient";
import useGlobalComponents from "@/setup/useGlobalComponents";
import "@/scss/global.scss";
import VueSignaturePad from "vue-signature-pad";
import SocioManifest from "../socio-manifest.json";
import PackageJson from "../package.json";
import AosComponents from "@socotec.io/socio-aos-component";
import { vuetifyProTipTap } from "./setup/tiptap";
import wb from "./registerServiceWorker";
import { createPinia } from "pinia";
import { createORM } from "pinia-orm";
import { createOidc, useOidcStore } from "vue3-oidc";
import { oidcSettings } from "@/setup/OIDCSettings";
import { createStore } from "vuex";
import VuexORM from "@vuex-orm/core";
import { globalEnv } from "@/constants";
import * as Sentry from "@sentry/vue";
import { getDefaultSentryConfig } from "@socotec.io/socio-grpc-api/configs";
import { createSentryPiniaPlugin } from "@sentry/vue";
import { WebStorageStateStore } from "oidc-client-ts";
import { applyRestApiErrorContext } from "@/setup/sentryRestContext";

const faroSettings = {
  enableTelemetry: import.meta.env.VITE_APP_ENABLE_TELEMETRY,
  grafanaApiKey: import.meta.env.VITE_APP_GRAFANA_API_KEY,
  collectorUrl: import.meta.env.VITE_APP_COLLECTOR_URL,
  propagateUrls: import.meta.env.VITE_APP_PROPAGATE_URLS,
  grpcApiEnv: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
  faroLogLevel: import.meta.env.VITE_APP_FARO_LOG_LEVEL,
};

createOidc({
  oidcSettings: oidcSettings,
  auth: false,
  events: {
    addUserLoaded: (user) => {
      useOidcStore().actions.value.setUser(user);
      setTelemetryUser(user, faroSettings.enableTelemetry);
    },
  },
  refreshToken: {
    enable: true,
    time: 30 * 1000,
  },
  userStore: new WebStorageStateStore({
    prefix: "vue3-oidc",
    store: window.sessionStorage,
  }),
});

const pinia = createPinia();
pinia.use(createORM());
pinia.use(createSentryPiniaPlugin());

const app = createApp(App);
app.use(pinia);

// Global properties
app.config.globalProperties.$workbox = wb;

app.use(SocioVueComponents, i18n);

const envConfig = {
  i18nLocale: globalEnv.i18nLocale,
  i18nFallbackLocale: globalEnv.i18nFallbackLocale,
  mapBoxApiKey: globalEnv.mapBoxApiKey,
  modelLinkerApiConfig: {
    basePath: import.meta.env.VITE_APP_MODEL_LINKER_REST_API_URL,
    accessToken: () => useOidcStore().state.value.token,
  },
  aosBackApiConfig: {
    basePath: import.meta.env.VITE_APP_AOS_REST_API_URL,
    accessToken: () => useOidcStore().state.value.token,
  },
};

const database = new VuexORM.Database();
const store = createStore({
  plugins: [VuexORM.install(database)],
});

const sentryConfig = getDefaultSentryConfig({
  app,
  dsn: "https://2acd9d0ae342d2afc0e076d4c5a35a76@sentry.tools.infra-socotec.net/9",
  router,
  release: `ssp@${import.meta.env.VITE_APP_VERSION}`,
  enabled: import.meta.env.MODE !== "development",
  environment: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
  integrations: [
    Sentry.unleashIntegration({
      featureFlagClientClass: UnleashClient,
      unleashClientClass: UnleashClient,
    }),
  ],
});

Sentry.init({
  ...sentryConfig,
  denyUrls: [/stonly\.com/],
  beforeSend: (event, hint) =>
    applyRestApiErrorContext(sentryConfig.beforeSend?.(event, hint) ?? event, hint),
});

app.use(router);
app.use(i18n);
app.use(vuetify);
app.use(store);
app.use(AosComponents, { store, envConfig, i18n });
app.use(VueSignaturePad);
useGlobalComponents(app);

initializeTracingAndLogging(SocioManifest, PackageJson, faroSettings, {
  router,
});
app.use(vuetifyProTipTap);

app.use(unleashPlugin, { unleashClient });

app.mount("#app");
